import type { ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Badge, Grid, Menu } from 'antd';
import { useAuth } from '../auth/AuthProvider';
import { useFeatures, type FeatureKey } from '../features/settings/api';

export interface SectionItem {
  path: string;
  label: string;
  icon: ReactNode;
  superAdminOnly?: boolean;
  /** hidden while this feature is switched off */
  feature?: FeatureKey;
  /** on a phone the page takes the whole screen, without the hub's menu (pages used on the go, like Scan cards) */
  phoneFullScreen?: boolean;
}

const useVisibleItems = (items: SectionItem[]) => {
  const { user } = useAuth();
  const features = useFeatures();
  return items.filter((i) => (!i.superAdminOnly || user?.role === 'SUPER_ADMIN') && (!i.feature || features.enabled(i.feature)));
};

/**
 * A hub page (Settings, Tools): its sub-pages listed on the left, the open one on the right.
 * Sub-pages keep their own PageHeader and `.page` wrapper; the layout only drops the wrapper's outer spacing.
 */
export function SectionLayout({ title, items, badges }: { title: string; items: SectionItem[]; badges?: Record<string, number> }) {
  const { pathname } = useLocation();
  const visible = useVisibleItems(items);
  const open = visible.find((i) => pathname === i.path || pathname.startsWith(`${i.path}/`));
  const selected = open?.path;
  const phone = Grid.useBreakpoint().md === false;
  if (phone && open?.phoneFullScreen) return <Outlet />;
  return (
    <div className="section-layout">
      <nav className="section-nav" aria-label={title}>
        <div className="section-nav-title">{title}</div>
        <Menu
          mode="inline"
          selectedKeys={selected ? [selected] : []}
          style={{ borderInlineEnd: 0, background: 'transparent' }}
          items={visible.map((i) => ({
            key: i.path,
            icon: i.icon,
            label: (
              <Link to={i.path}>
                {i.label}
                {!!badges?.[i.path] && <Badge count={badges[i.path]} size="small" color="#2F5BEA" style={{ marginLeft: 8 }} />}
              </Link>
            ),
          }))}
        />
      </nav>
      <div className="section-content">
        <Outlet />
      </div>
    </div>
  );
}

/** Opens a hub's first sub-page the user may see. */
export function SectionIndex({ items }: { items: SectionItem[] }) {
  const visible = useVisibleItems(items);
  return <Navigate to={visible[0]?.path ?? '/'} replace />;
}

/** Sends an old URL to its new home, keeping the rest of the path and the query string. */
export function MovedTo({ from, to }: { from: string; to: string }) {
  const { pathname, search } = useLocation();
  return <Navigate to={`${to}${pathname.slice(from.length)}${search}`} replace />;
}
